import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { cludoRequiredIdsAndClasses } from 'helpers/cludo';
import { decodeQueryParamString } from 'helpers/string/string';
import { useEffect, useRef, useState } from 'react';
import { CludoSearch } from 'ui/components/3-organisms/CludoSearch';

export const HeroSearchFeature: React.FC<Content.HeroSearch> = ({ content }) => {
	const SiteContext = useSiteContext();
	const PageContext = usePageContext();
	const { themeSettings } = PageContext;
	const dictionary = useDictionaryContext();
	const { heading, image, lead, link, customerId, engineId } = content?.properties ?? {};
	const [isLoading, setIsLoading] = useState(false);
	const [currentQuery, setCurrentQuery] = useState<UI.CludoSearchResponse>();
	const [currentCategory, setCurrentCategory] = useState('');
	const { formId, searchResultsId } = cludoRequiredIdsAndClasses;

	// Kept in a ref, so the search callback always uses the current texts without re-initializing the search
	const currentPaginationLabels = {
		navigation: dictionary.getValue('Search.ResultList.PaginationNavigationAriaLabel', null, 'Sidenavigation'),
		previous: dictionary.getValue('Search.ResultList.PaginationPreviousAriaLabel', null, 'Forrige side'),
		next: dictionary.getValue('Search.ResultList.PaginationNextAriaLabel', null, 'Næste side'),
		currentPage: dictionary.getValue('Search.ResultList.PaginationCurrentPage', null, 'nuværende side'),
	};
	const paginationLabels = useRef(currentPaginationLabels);
	paginationLabels.current = currentPaginationLabels;

	const hero = {
		heading,
		lead,
		image: { url: image?.url, altText: '' },
		link,
		theme: themeSettings?.heroAndBreadcrumbColorTheme,
	};

	const cludoDictionary = {
		categoryFilter: {
			toggleButtonText: dictionary.getValue('Search.Filter.SubjectToggle', null, 'Select topic'),
			dropdownAriaLabel: dictionary.getValue('Search.Filter.SubjectToggle', null, 'Select topic'),
		},
		searchFilter: {
			heading: dictionary.getValue('Search.Filter.Heading', null, 'Searching and filtering'),
			searchPlaceholder: dictionary.getValue('Search.Filter.SearchPlaceholder', null, 'Enter keyword'),
			clearSearchBtn: dictionary.getValue('Search.Filter.Clear', null, 'Clear search'),
			submitBtn: dictionary.getValue('Search.Filter.Submit', null, 'Search'),
		},
		noScriptTxt: dictionary.getValue(
			'Accessibility.NoJavaScript.Component',
			null,
			'Din browser understøtter ikke JavaScript hvilket kræves for dette komponent.',
		),
	};

	useEffect(() => {
		// The pagination comes from the search template with English texts, so they are replaced after each search
		const localizePagination = () => {
			const navigation = document.querySelector(`#${searchResultsId} .cludo-page-navigation`);
			if (!navigation) return;

			const { current: labels } = paginationLabels;
			navigation.setAttribute('aria-label', labels.navigation);
			navigation.querySelector('.previous a')?.setAttribute('aria-label', labels.previous);
			navigation.querySelector('.next a')?.setAttribute('aria-label', labels.next);
			navigation.querySelectorAll('.cludo-sr-only').forEach((item) => {
				item.textContent = `(${labels.currentPage})`;
			});
		};

		const cludoSearchSettings: UI.CludoSearchSettings = {
			customerId,
			engineId,
			searchUrl: location.pathname,
			language: SiteContext.culture ? new Intl.Locale(SiteContext.culture)?.language : 'en',
			canUserBeTracked: function () {
				if (SiteContext.cookieConsentGiven !== undefined) {
					return SiteContext.cookieConsentGiven;
				}
				return false;
			},
			searchInputs: [formId],
			customerTemplate: 'MST 2023',
			translateSearchTemplates: true,
			focusOnResultsAfterSearch: true,
			jumpToTopOnFacetClick: false,
			customCallbackBeforeSearch: () => setIsLoading(true),
			customCallbackAfterSearch: (response) => {
				setIsLoading(false);
				setCurrentQuery(response);
				localizePagination();
				const params = new URLSearchParams(location.href);
				setCurrentCategory(params.get('cludoCategory'));
			},
		};

		// Load the Cludo script.
		const script = document.createElement('script');
		script.src = 'https://customer.cludo.com/scripts/bundles/search-script.min.js';
		script.async = true;
		document.body.appendChild(script);

		script.addEventListener('load', () => {
			const CludoSearch = new (window as UI.CludoWindow).Cludo(cludoSearchSettings);
			CludoSearch.init();
			// Trim the search string for search-result-count in SearchResultList
			CludoSearch.params.query = decodeQueryParamString(CludoSearch.params.query);
		});

		return () => {
			// Clean up the Cludo script when the component unmounts
			document.body.removeChild(script);
		};
	}, [formId, searchResultsId, customerId, engineId, SiteContext.culture, SiteContext.cookieConsentGiven]);

	return (
		<CludoSearch
			hero={hero}
			searchQuery={{ isLoading, queryId: currentQuery?.QueryId, currentCategory }}
			dictionary={cludoDictionary}
		/>
	);
};
